import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function VerifyEmail({ setUser, checkAuth }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // "verifying", "success", "error"
  const [errorMessage, setErrorMessage] = useState("");
  const [userData, setUserData] = useState(null);
  const isProcessingRef = useRef(false);

  const verifyEmail = useCallback(async (token) => {
    try {
      const url = `${API}/auth/verify-email?token=${encodeURIComponent(token)}`;
      
      // Use XMLHttpRequest to avoid fetch body stream issues
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("GET", url, true);
        xhr.withCredentials = true;
        xhr.setRequestHeader("Accept", "application/json");
        
        xhr.onload = function() {
          console.log("XHR Response status:", xhr.status);
          try {
            const responseData = JSON.parse(xhr.responseText);
            console.log("XHR Response data:", responseData);
            
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(responseData);
            } else {
              reject(new Error(responseData.detail || "Verification failed"));
            }
          } catch (e) {
            reject(new Error("Invalid response from server"));
          }
        };
        
        xhr.onerror = function() {
          reject(new Error("Network error occurred"));
        };
        
        xhr.send();
      });

      setStatus("success");
      setUserData(data);
      toast.success("Email verified successfully! You're now logged in.");

      // Update app auth state with the returned user data
      if (setUser && data.user_id) {
        setUser({
          user_id: data.user_id,
          email: data.email,
          name: data.name,
          nickname: data.nickname,
          role: data.role,
          is_admin: data.is_admin,
          total_score: data.total_score || 0
        });
      }

      // Also trigger checkAuth to ensure session cookie is validated
      if (checkAuth) {
        // Small delay to ensure cookie is set
        setTimeout(() => {
          checkAuth();
        }, 500);
      }

      // Don't auto-redirect - let user click the button
    } catch (error) {
      console.error("Verification error:", error);
      setStatus("error");
      setErrorMessage(error.message);
    }
  }, [setUser, checkAuth]);

  useEffect(() => {
    const token = searchParams.get("token");
    
    if (!token) {
      setStatus("error");
      setErrorMessage("Invalid verification link. No token provided.");
      return;
    }

    // Prevent double execution
    if (isProcessingRef.current) {
      return;
    }
    isProcessingRef.current = true;
    
    verifyEmail(token);
  }, [searchParams, verifyEmail]);

  const goToHome = async () => {
    // Trigger auth check to ensure user is logged in via session cookie
    if (checkAuth) {
      try {
        await checkAuth();
      } catch (error) {
        console.error("Auth check failed after verification:", error);
        // If auth check fails, the user might need to log in manually
        // This can happen if cookies aren't being received properly
        toast.info("Please log in to continue");
      }
    }
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-card border border-border rounded-2xl p-8 shadow-lg text-center">
          {/* Verifying State */}
          {status === "verifying" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-primary animate-spin" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Verifying Your Email</h1>
              <p className="text-muted-foreground">
                Please wait while we verify your email address...
              </p>
            </>
          )}

          {/* Success State */}
          {status === "success" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
              </div>
              <h1 className="text-2xl font-bold mb-2 text-green-600 dark:text-green-400">
                Email Verified!
              </h1>
              <p className="text-muted-foreground mb-6">
                {userData?.name ? `Welcome, ${userData.name}!` : "Your email has been verified."}
                <br />
                You're now logged in and ready to start coding.
              </p>
              
              <Button 
                onClick={goToHome} 
                className="w-full h-12 rounded-xl"
                data-testid="go-to-app-btn"
              >
                Start Coding Now
              </Button>
            </>
          )}

          {/* Error State */}
          {status === "error" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-600 dark:text-red-400" />
              </div>
              <h1 className="text-2xl font-bold mb-2 text-red-600 dark:text-red-400">
                Verification Failed
              </h1>
              <p className="text-muted-foreground mb-6">
                {errorMessage}
              </p>
              
              <div className="bg-muted/50 rounded-xl p-4 mb-6 text-sm text-muted-foreground">
                <p>This can happen if:</p>
                <ul className="list-disc list-inside mt-2 text-left">
                  <li>The link has expired (links are valid for 24 hours)</li>
                  <li>The link has already been used</li>
                  <li>The link was copied incorrectly</li>
                </ul>
              </div>
              
              <div className="space-y-3">
                <Button 
                  onClick={goToHome} 
                  className="w-full h-12 rounded-xl"
                  data-testid="go-home-btn"
                >
                  Go to Home
                </Button>
                <p className="text-xs text-muted-foreground">
                  Try logging in again to receive a new verification email.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Branding */}
        <div className="text-center mt-6">
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-primary">CodingDrills</span>.au
          </p>
        </div>
      </div>
    </div>
  );
}
